// OpenTelemetry configuration for the web form (DS-3899).
//
// Everything that tunes the telemetry lives here, in one place.

export const SERVICE_NAME = 'datascope-web-form';

// Collector endpoint. Its absence is the kill switch: with no place to export to, telemetry
// is never initialized at all, so no provider, no instrumentation and no listeners.
// Production ships without it, and each environment is turned on by defining it in that
// environment's build.
export const COLLECTOR_URL = process.env.REACT_APP_OTEL_COLLECTOR_URL;

// Full traces endpoint, used both as the exporter target and as the URL the fetch and XHR
// instrumentations have to ignore. It lives here so the two can never drift apart: the
// exporter itself sends over fetch, so tracing its own requests would feed the pipeline
// with the spans it just produced. `ignoreUrls` compares plain strings for equality, so
// the base URL alone would not match this path.
// Trailing slashes are trimmed because a collector URL copied from a browser or from
// Terraform output usually carries one, and `https://host//v1/traces` is a 404 on many OTLP
// receivers. That failure is silent: telemetry never reports its own delivery problems.
export const buildTracesEndpoint = (collectorUrl: string): string =>
  `${collectorUrl.replace(/\/+$/, '')}/v1/traces`;

export const TRACES_ENDPOINT = COLLECTOR_URL ? buildTracesEndpoint(COLLECTOR_URL) : undefined;

// Deployment environment. The edge collector drops anything that does not declare service,
// environment and version, so this has to be a declared value and never an inferred one:
// NODE_ENV does not tell staging from production, and guessing from the hostname breaks
// silently whenever a domain changes.
export const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;

// Backend this app talks to. Everything else the page contacts is a third party.
export const BACKEND_URL = process.env.REACT_APP_BACKEND_ENDPOINT;

// A URL as an anchored regex fragment. Every pattern below derives from this, so the set of
// URLs we trace and the set we send trace context to can never drift apart. The `(/|$)` tail
// is what stops a lookalike host: `staging.mydatascope.com.evil.test` starts with the backend
// as a plain string but is somebody else's domain.
const anchoredPattern = (url: string): string =>
  `^${url.replace(/\/+$/, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(/|$)`;

// URLs the fetch and XHR instrumentations must not trace.
//
// This is an allow list expressed as a deny pattern, and the inversion is the point: the
// page loads around thirty hosts and only the backend is ours to act on.
//
// Two different things end up excluded. Most are analytics and support widgets (Clarity,
// HubSpot, Analytics, Intercom, Smartlook) whose latency we cannot fix and whose polling
// says nothing about this app. Firestore is the exception worth naming: it is real product
// infrastructure, not a third party, but its `Listen` channel holds a long poll open for
// 30 seconds by design, so recording it as a request would put a 30 second floor under
// every latency percentile of this service. Both are left out for now; whether Firebase
// deserves its own treatment is a question for the dashboards work (DS-3898).
//
// Filtering here rather than at the collector means the span is never created: nothing is
// serialised, nothing leaves the user's machine and nothing is paid for.
//
// The collector's own endpoint is listed in both branches, and listing it alongside the deny
// pattern is not redundant. That pattern only excludes what is *not* the backend, so a
// deployment that puts the collector under the backend's origin, which is the obvious way to
// avoid a second CORS origin, would start tracing the exporter's own requests: each export
// produces a span, which gets batched and exported, which produces another, and it never
// stops. Listing it costs nothing and keeps an assumption about topology out of this code.
export function buildIgnoreUrls(
  backendUrl: string | undefined,
  tracesEndpoint: string,
): Array<string | RegExp> {
  if (!backendUrl) return [tracesEndpoint];

  // Negative lookahead: matches every URL that does NOT start with the backend, and a match
  // in `ignoreUrls` means "do not trace".
  return [tracesEndpoint, new RegExp(`^(?!${anchoredPattern(backendUrl).slice(1)})`)];
}

// URLs that carry the W3C `traceparent` header, which is what lets a browser span become the
// parent of the Rails span and turns two disconnected traces into one (DS-3900).
//
// The browser only adds the header cross-origin when the URL is listed here, and this app is
// exactly that case: it runs on `iframes.staging.mydatascope.com` while the backend answers
// on `staging.mydatascope.com`. Rails already extracts the context and its CORS allows the
// header, so listing the backend is the whole change.
//
// Restricted to the backend on purpose. Propagating widely would leak trace identifiers to
// Clarity, HubSpot and every other host the page talks to, and would add a header to requests
// whose CORS policy we do not control, which can turn a working call into a failing preflight.
// With no backend declared, nothing is propagated: the safe direction is to send less.
export function buildPropagateUrls(backendUrl: string | undefined): Array<string | RegExp> {
  if (!backendUrl) return [];

  return [new RegExp(anchoredPattern(backendUrl))];
}

// Resource URLs worth a span, as an allow list this time and not a deny pattern.
//
// `document-load` creates one span per entry of the browser's resource timeline and accepts
// no URL filter at all, so `buildIgnoreUrls` never reaches those spans. They get filtered
// after the fact instead, in `FilteringSpanProcessor`.
//
// The list is wider than the one for fetch and XHR on purpose. Our own assets are the load
// time this card exists to measure, so `main.js`, the stylesheet and the scripts under
// `public/` stay. What goes is the third parties `public/index.html` pulls in: Tag Manager,
// Google Fonts, Smartlook, Beamer and HubSpot. Their download time is not ours to fix, and a
// span per asset per visit is volume with nothing to say.
export function buildTracedResourceUrls(
  backendUrl: string | undefined,
  appOrigin: string,
): RegExp[] {
  const patterns = [new RegExp(anchoredPattern(appOrigin))];

  if (backendUrl) patterns.push(new RegExp(anchoredPattern(backendUrl)));

  return patterns;
}

// Share of traces recorded, between 0 and 1. Changing it requires a deploy.
//
// At 1.0 while this lives in staging: every load leaves a trace, which is what makes QA
// workable, and uncaught errors are always recorded, which is what the PRD demands. Lower it
// when production is turned on, and revisit whether error traces need a sampler of their own.
export const SAMPLING_RATIO = 1.0;
